<script lang="ts" setup>
import type { CategoryTree } from "~~/models/categories";
import type { CatalogPageRecord, ProductGroupsQuery_allProductGroups_ProductGroupRecord } from "~~/server/graphql/datocms/generated";

const { cmsData, categoryTree, productGroups } = defineProps<{
  cmsData: CatalogPageRecord;
  categoryTree: CategoryTree;
  productGroups?: Array<ProductGroupsQuery_allProductGroups_ProductGroupRecord> | null | undefined;
}>();

const { t } = useI18n();

const isOpen = ref(false);

const selectedCategoryId = ref(-1);
const selectedCategory = computed(() => categoryTree.getFirstLevel().find(c => c.id === selectedCategoryId.value));

const triggerIcon = computed(() => isOpen.value ? "i-ph-x" : "i-ph-caret-down");

const categoryMenu = ref(null);
onClickOutside(categoryMenu, _ => isOpen.value = false);

function getCategoryIcon(categoryId: number) {
  return productGroups
    ?.filter((pg: ProductGroupsQuery_allProductGroups_ProductGroupRecord) => pg.optCategory)
    ?.find((pg: ProductGroupsQuery_allProductGroups_ProductGroupRecord) => (pg.optCategory! as Array<{ name: string; value: string }>)
      .map((d: { name: string; value: string }) => d.value)
      .includes(categoryId.toString()))
    ?.icon;
}
</script>

<template>
  <div ref="categoryMenu" class="relative z-40">
    <div>
      <UButton variant="solid" color="blue" size="md" :ui="{ base: 'w-full' }" :icon="triggerIcon" trailing @click="isOpen = !isOpen">
        {{ cmsData.actionShowAllCategories }}
      </UButton>
    </div>

    <div v-if="isOpen" class="p-2xs absolute top-[45px] w-full rounded-lg bg-[#F3F1EC] md:top-[55px]">
      <div class="pg-lg rounded-lg">
        <ul v-if="!selectedCategory">
          <li
            v-for="category of categoryTree.getFirstLevel()" :key="category.id"
            class="category-menu__sub-menu pb-2xs last:pb-0"
            @click="selectedCategoryId = category.id"
          >
            <div class="p-xs flex cursor-pointer items-center justify-between rounded-lg bg-white hover:bg-[#F8F7F4]">
              <div class="gap-sm flex shrink-0 items-center">
                <img v-if="getCategoryIcon(category.id)" :src="getCategoryIcon(category.id)?.url" alt="" class="size-[32px] shrink-0">
                <span class="text-sm font-bold">{{ categoryTree.getName(category.id) }}</span>
              </div>
              <UIcon name="i-ph-caret-right" />
            </div>
          </li>

          <li class="pb-2xs last:pb-0">
            <NuxtLinkLocale to="/happy-deals">
              <div class="p-xs flex cursor-pointer items-center justify-between rounded-lg bg-white hover:bg-[#F8F7F4]">
                <div class="gap-sm flex shrink-0 items-center">
                  <img src="/icons/promo.svg" alt="" class="size-[32px] shrink-0">
                  <span class="text-primary text-sm font-bold">{{ cmsData.happyDealsCategoryTitle }}</span>
                </div>
                <UIcon name="i-ph-caret-right" class="text-primary" />
              </div>
            </NuxtLinkLocale>
          </li>
        </ul>

        <div v-if="selectedCategory" class="rounded-lg bg-white">
          <UButton variant="link" color="neutral" size="md" icon="i-ph-caret-left" @click="selectedCategoryId = -1">
            {{ t("breadcrumb.back") }}
          </UButton>

          <div class="p-md">
            <h4 class="text-blue mb-lg">
              <NuxtLinkLocale class="hover:underline" :to="`/catalog${categoryTree.getPath(selectedCategory.id)}`">
                {{ categoryTree.getName(selectedCategory.id) }}
              </NuxtLinkLocale>
            </h4>

            <ul v-if="selectedCategory" class="gap-3xl flex flex-col">
              <li v-for="child of categoryTree.getChildren(selectedCategory.id)" :key="child.id" class="gap-2xs flex flex-col">
                <NuxtLinkLocale class="font-semibold hover:underline" :to="`/catalog${categoryTree.getPath(child.id)}`">
                  {{ categoryTree.getName(child.id) }}
                </NuxtLinkLocale>

                <ul class="gap-2xs flex flex-col">
                  <li v-for="subChild of categoryTree.getChildren(child.id)" :key="subChild.id">
                    <NuxtLinkLocale class="hover:underline" :to="`/catalog${categoryTree.getPath(subChild.id)}`">
                      {{ categoryTree.getName(subChild.id) }}
                    </NuxtLinkLocale>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
